import { type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { AppShell, RailBrand, RailFoot, SliderzMark, Topbar, TopbarSearch, alert, signOut } from '@orgazone/ui-web';
import { PAGE_PAD, RAIL_BRAND, RAIL_NAV } from '@orgazone/ui-tokens';
import { Icon, type IconName } from './icons';
import { useApp } from '../state';

// Coquille de la console EDI — PRIMITIVES PLATEFORME (§24.9sexies-ter, bascule 2026-08-05) :
// AppShell (rail fixe, seul le contenu défile), RailBrand (marque), RailFoot (modèle Store),
// Topbar (recherche + santé en slots, bascule thème intégrée). Actif de nav = aplat marque-700
// (décision 2026-08-05). ⚠️ §23.0 : pas de badge de comptage tant qu'aucun compte RÉEL ne l'alimente.

interface NavEntry { to: string; label: string; icon: IconName }
const GROUPS: { title?: string; items: NavEntry[] }[] = [
  { items: [
    { to: '/', label: 'Supervision', icon: 'grid' },
    { to: '/journal', label: 'Journal des échanges', icon: 'layers' },
  ] },
  { title: 'Configuration', items: [
    { to: '/fournisseurs', label: 'Fournisseurs', icon: 'building' },
    { to: '/clients', label: 'Clients', icon: 'pin' },
    { to: '/mappings', label: 'Mappings', icon: 'sparkles' },
  ] },
  { title: 'Système', items: [
    { to: '/transports', label: 'Catalogue transports', icon: 'swap' },
    { to: '/certificats', label: 'Certificats AS2', icon: 'lock' },
    { to: '/utilisateurs', label: 'Utilisateurs', icon: 'users' },
    { to: '/maintenance', label: 'Maintenance', icon: 'save' },
    { to: '/parametres', label: 'Paramètres', icon: 'cog' },
  ] },
];

/*
 * ⚠️ 🔴 PLUS DE TABLE DE TITRES PAR CHEMIN : elle n'alimentait que le fil d'Ariane de la barre, retiré
 * avec lui. C'était une SECONDE écriture du titre de chaque écran — la première étant le `PageHead` de
 * l'écran lui-même —, donc deux sources qui auraient fini par ne plus dire la même chose, après avoir
 * commencé par le dire deux fois à l'écran.
 */
export function Shell({ children }: { children: ReactNode }) {
  const { admin, theme, toggleTheme, user, showToast, logout } = useApp();
  const nav = useNavigate();

  const onSearch = (q: string) => {
    const t = q.trim();
    if (!t) { showToast('Saisissez un fichier, un id de message ou un GLN', 'warn'); return; }
    nav(`/journal?q=${encodeURIComponent(t)}`);
    showToast(`Journal filtré sur « ${t} »`);
  };

  return (
    <AppShell
      rail={<>
        {/* ⚠️ AGENCEMENT `product` ET MONOGRAMME SLIDERZ (arbitrage utilisateur) : la maquette met
            « Sliderz » au-dessus et « EDI » dessous en mono capitales, comme le back-office — l'EDI
            appartient à la chaîne du PRODUIT (il alimente les catalogues du commerçant), là où la
            Console, le store privé et l'IdP sont les surfaces de l'ÉDITEUR et gardent la marque
            Orgazone. Métriques du relevé : monogramme 28×29, le reste porté par la charte.
            ⚠️ Sans pastille de fond : le logo porte déjà sa forme et sa couleur — l'enfermer dans un
            carré turquoise en écrasait le dessin. */}
        <RailBrand
          layout="product"
          logo={<SliderzMark size={RAIL_BRAND.subtitle.logoWidth} height={RAIL_BRAND.subtitle.logoHeight} />}
          surtitle="EDI"
          name="Sliderz"
        />
        {/* ⚠️ PLUS DE REMBOURRAGE HORIZONTAL ICI (2026-09-08) : il vient de l'aside (RAIL_PADDING de la
            charte). Le garder l'additionnerait, et les items partiraient à 34 du bord au lieu de 24.
            ⚠️ LE FILET DE LA TÊTE DE RAIL EST ICI (`navEdge`), pas sous la marque : c'est le relevé, et
            c'est ce qui le met EN RETRAIT du rembourrage de l'aside au lieu de courir d'un bord à
            l'autre. L'agencement `product` n'en pose donc aucun. */}
        <nav style={{ flex: 1, overflowY: 'auto', paddingBottom: 10, ...RAIL_BRAND.subtitle.navEdge }}>
          {/* ⚠️ RIEN AU-DESSUS DU PREMIER GROUPE : au relevé, le premier item est à 10 du filet — la
              respiration vient du `paddingTop` du nav, et un `marginTop` de plus s'y AJOUTERAIT. Les
              suivants portent les 14 qui séparent deux groupes, comme la maquette. */}
          {GROUPS.map((g, gi) => (
            <div key={gi} style={{ marginTop: gi === 0 ? 0 : 14 }}>
              {g.title && <div style={{ fontSize: 'var(--t-micro)', fontWeight: 600, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--sidebar-text-dim)', padding: '0 12px 6px' }}>{g.title}</div>}
              {/* ⚠️ CLASSE `.nav` DE LA CHARTE (2026-09-04) — cet item était la TROISIÈME écriture de la
                  même chose : deux feuilles d'app la définissaient en CSS, celle-ci la rejouait en
                  style calculé. C'est elle qui avait perdu le survol et l'enfoncement au clic, parce
                  qu'un style en ligne ne sait pas exprimer un pseudo-état. */}
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.to === '/'} style={{ marginBottom: 1 }}
                  className={({ isActive }) => 'nav' + (isActive ? ' active' : '')}>
                  <Icon name={it.icon} size={RAIL_NAV.iconSize} />
                  <span style={{ flex: 1 }}>{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <RailFoot
          name={user.name}
          role={admin ? 'Admin' : 'Lecture seule'}
          onLogout={() => void signOut(logout, { onError: (message) => void alert({ title: 'Déconnexion impossible', message }) })}
        />
      </>}
      header={<>
        {/* ⚠️ EN-TÊTE FIXE de la coquille : la barre ne défile pas avec la page. */}
        {/* ⚠️ 🔴 NI FIL DE PAGE NI SANTÉ DANS LA BARRE — relevé de la maquette, décision utilisateur.
            (1) Le fil « Exploitation / <titre> » n'existe pas dans le dessin : « Exploitation »
            y est le SURTITRE DU RAIL de la variante Orgazone, et le titre de l'écran est porté par son
            `PageHead`. Le garder écrivait le titre DEUX FOIS à quarante pixels d'écart, sur neuf écrans
            sur dix — redondance que la pose des `PageHead` manquants venait de rendre complète.
            (2) La santé du service quitte la barre : l'exploitant la lit sur la Supervision, dont c'est
            le sujet. ⚠️ Ce qu'on y perd est ÉCRIT : il n'existe plus de témoin PERMANENT que le service
            répond, donc une panne ne se voit qu'en ouvrant cet écran (`ECARTS-beta-vs-maquette.md`). */}
        <Topbar mode={theme} onToggleTheme={toggleTheme}>
          <TopbarSearch placeholder="Fichier, id de message, GLN…" label="Rechercher un fichier, un identifiant de message ou un GLN"
            onKeyDown={(e) => { if (e.key === 'Enter') onSearch((e.target as HTMLInputElement).value); }} />
        </Topbar>

        {/* Bandeau lecture seule */}
        {!admin && (
          <div style={{ background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-body)', padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--ink-200)', flexShrink: 0 }}>
            <Icon name="eye" size={15} />
            Lecture seule. Les actions (rejouer, mettre en pause, tester, éditer) sont indisponibles et les secrets restent masqués.
          </div>
        )}
      </>}
    >
      {/* ⚠️ Respiration de la charte — cette page posait 20 px sur les quatre côtés, soit un titre
          désaligné de 8 px avec les deux autres outils. */}
      <main style={{ flex: 1, padding: `${PAGE_PAD.top}px ${PAGE_PAD.side}px ${PAGE_PAD.bottom}px` }}>{children}</main>
    </AppShell>
  );
}
